import { PropTypes } from 'prop-types'
import React from 'react'
import './buttonLinkCtaList.css'
import DOMPurify from 'dompurify'
const LINK_TYPES = {
  PRIMARY_BTN: 'primaryBtn',
  BRAND_PRIMARY_BTN: 'brandPrimaryBtn',
  RASPBERRY_PRIMARY_BTN: 'raspberryPrimaryBtn',
  AQUA_PRIMARY_BTN: 'aquaPrimaryBtn',
  YELLOW_PRIMARY_BTN: 'yellowPrimaryBtn',
  TEAL_PRIMARY_BTN: 'tealPrimaryBtn',
  SECONDARY_BTN: 'secondaryBtn',
  TERTIARY_BTN: 'tertiaryBtn',
  LINK: 'link',
  LINK_WITH_ARROW: 'linkWithArrow',
}

const LINK_DISPLAY_TYPES = {
  INLINE: 'inline',
  BLOCK: 'block',
}

const MOBILE_DISPLAY_TYPES = {
  DEFAULT: 'default',
  INLINE: 'inline',
  BLOCK: 'block',
}

const LINK_WIDTH_TYPES = {
  AUTO: 'auto',
  FULL: 'full',
}

const MOBILE_WIDTH_TYPES = {
  DEFAULT: 'default',
  AUTO: 'auto',
  FULL: 'full',
}

const CTA_WIDTH_CLASS = {
  [LINK_WIDTH_TYPES.AUTO]: 'buttonLinkCtaList-widthAuto',
  [LINK_WIDTH_TYPES.FULL]: 'buttonLinkCtaList-widthFull',
}

const CTA_MOBILE_WIDTH_CLASS = {
  [MOBILE_WIDTH_TYPES.AUTO]: 'buttonLinkCtaList-mobileWidthAuto',
  [MOBILE_WIDTH_TYPES.FULL]: 'buttonLinkCtaList-mobileWidthFull',
}

const ButtonLinkCtaList = ({
  linkList = [],
  ctaWrapperClassNames = '',
  linkClassNames = '',
  btnClassNames = '',
}) => {
  const getStyles = (type, reversed) => {
    const linkReversed = reversed ? 'Link--reversed' : ''
    const buttonReversed = reversed ? 'Button--reversed' : ''

    switch (type) {
      case LINK_TYPES.PRIMARY_BTN:
        return `Button Button--primary ${buttonReversed}`
      case LINK_TYPES.BRAND_PRIMARY_BTN:
        return `Button Button--primary Button--color--brand ${buttonReversed}`
      case LINK_TYPES.RASPBERRY_PRIMARY_BTN:
        return `Button Button--primary Button--color--raspberry ${buttonReversed}`
      case LINK_TYPES.AQUA_PRIMARY_BTN:
        return `Button Button--primary Button--color--aqua ${buttonReversed}`
      case LINK_TYPES.YELLOW_PRIMARY_BTN:
        return `Button Button--primary Button--color--yellow ${buttonReversed}`
      case LINK_TYPES.TEAL_PRIMARY_BTN:
        return `Button Button--primary Button--color--teal ${buttonReversed}`
      case LINK_TYPES.SECONDARY_BTN:
        return `Button Button--secondary ${buttonReversed}`
      case LINK_TYPES.TERTIARY_BTN:
        return `Button Button--tertiary ${buttonReversed}`
      case LINK_TYPES.LINK:
        return `Link u-posRelative hover-underline-animation ${linkReversed}`
      case LINK_TYPES.LINK_WITH_ARROW:
        return `Link u-posRelative hover-underline-animation ${linkReversed}`
      default:
        return `Link u-posRelative hover-underline-animation ${linkReversed}`
    }
  }

  const getWidthClassName = (width, mobileWidth) => {
    const widthClass =
      CTA_WIDTH_CLASS[width] || CTA_WIDTH_CLASS[LINK_WIDTH_TYPES.AUTO]
    const mobileWidthClass = CTA_MOBILE_WIDTH_CLASS[mobileWidth] || ''
    return `${widthClass} ${mobileWidthClass}`.trim()
  }

  const getCtaContainerClassNames = (type, width, mobileWidth) => {
    const containerClassNames = [
      'cta-list-item',
      isLink(type) ? 'cta-list-item--link' : 'cta-list-item--btn',
    ]

    if (width === LINK_WIDTH_TYPES.FULL) {
      containerClassNames.push('cta-list-item--full')
    }

    if (mobileWidth === MOBILE_WIDTH_TYPES.FULL) {
      containerClassNames.push('cta-list-item--mobile-full')
    } else if (mobileWidth === MOBILE_WIDTH_TYPES.AUTO) {
      containerClassNames.push('cta-list-item--mobile-auto')
    }

    return containerClassNames.join(' ')
  }

  const isLink = (type) => {
    return [LINK_TYPES.LINK, LINK_TYPES.LINK_WITH_ARROW].includes(type)
  }

  const handleBtnClick = (link, e) => {
    e.stopPropagation()
    e.preventDefault()
    if (link?.url) {
      if (link?.target === '_blank') {
        window.open(link.url, '_blank')
      } else {
        window.location.href = DOMPurify.sanitize(link.url)
      }
    }
  }

  return (
    <>
      {linkList?.length > 0 ? (
        <div
          className={`buttonLinkCtaList u-flex flex-row u-flexWrap ${ctaWrapperClassNames}`}
        >
          {linkList?.map((link = {}, index) => {
            const showBreak = link?.display === LINK_DISPLAY_TYPES.BLOCK
            const mobileDisplay = link?.mobileDisplay
            const hasMobileOverride =
              mobileDisplay && mobileDisplay !== MOBILE_DISPLAY_TYPES.DEFAULT

            let breakClassName = 'buttonLinkCtaList-break'
            if (hasMobileOverride) {
              if (showBreak && mobileDisplay !== MOBILE_DISPLAY_TYPES.BLOCK) {
                breakClassName =
                  'buttonLinkCtaList-break buttonLinkCtaList-break--desktopOnly'
              } else if (
                !showBreak &&
                mobileDisplay === MOBILE_DISPLAY_TYPES.BLOCK
              ) {
                breakClassName =
                  'buttonLinkCtaList-break buttonLinkCtaList-break--mobileOnly'
              }
            }

            const shouldShowBreak =
              showBreak ||
              (hasMobileOverride &&
                mobileDisplay === MOBILE_DISPLAY_TYPES.BLOCK)

            return (
              <React.Fragment key={index}>
                {shouldShowBreak && <div className={breakClassName} />}
                <div
                  className={getCtaContainerClassNames(
                    link?.type,
                    link?.width,
                    link?.mobileWidth
                  )}
                >
                  {isLink(link?.type) ? (
                    <a
                      href={DOMPurify.sanitize(link?.url)}
                      target={link?.target}
                      data-analytics={link?.analyticsAttribute}
                      className={`u-flex flex-row u-flexJustifyCenter ${linkClassNames} ${getStyles(link?.type, link?.reversed)} ${getWidthClassName(link?.width, link?.mobileWidth)}`}
                    >
                      {link?.text}

                      {link?.type === LINK_TYPES.LINK_WITH_ARROW && (
                        <span className={`arrow Icon--material`}>
                          navigate_next
                        </span>
                      )}
                    </a>
                  ) : (
                    <button
                      href={DOMPurify.sanitize(link?.url)}
                      target={link?.target}
                      data-analytics={link?.analyticsAttribute}
                      className={`u-flex flex-row u-flexJustifyCenter ${btnClassNames} ${getStyles(link?.type, link?.reversed)} ${getWidthClassName(link?.width, link?.mobileWidth)}`}
                      onClick={(e) => handleBtnClick(link, e)}
                    >
                      {link?.text}
                    </button>
                  )}
                </div>
              </React.Fragment>
            )
          })}
        </div>
      ) : (
        ''
      )}
    </>
  )
}

ButtonLinkCtaList.propTypes = {
  linkList: PropTypes.array,
}

export default ButtonLinkCtaList
